import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { fetchSmfFlights } from '../services/aviationApi';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

export default function Flights() {
  const [flights, setFlights] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadFlights();
    const interval = setInterval(loadFlights, 60000);
    return () => clearInterval(interval);
  }, []);

  const loadFlights = async () => {
    setLoading(true);
    const data = await fetchSmfFlights();
    setFlights(data);
    setLoading(false);
  };

  const getStatusColor = (status) => {
    const s = status?.toLowerCase();
    if (s === 'en-route' || s === 'active') return 'text-emerald-400 bg-emerald-400/10';
    if (s === 'scheduled' || s === 'started') return 'text-blue-400 bg-blue-400/10';
    if (s === 'landed' || s === 'arrived') return 'text-slate-400 bg-slate-400/10';
    if (s === 'cancelled') return 'text-rose-400 bg-rose-400/10';
    return 'text-amber-400 bg-amber-400/10';
  };

  return (
    <div className="p-4 pb-24 md:pb-8 space-y-4">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-white tracking-tight">SMF Live Feed</h2>
          <p className="text-slate-500 text-xs font-medium uppercase tracking-wider">Active Departures</p>
        </div>
        <button 
          onClick={loadFlights} 
          disabled={loading}
          className={`p-2 bg-slate-800 rounded-full text-amber-400 transition-all duration-500 ${loading ? 'animate-spin opacity-50' : 'hover:rotate-180'}`}
        >
          <SafeIcon icon={FiIcons.FiRefreshCw} />
        </button>
      </div>

      {loading && flights.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-slate-500">
          <div className="animate-pulse mb-4">
            <SafeIcon icon={FiIcons.FiAirplay} className="text-4xl text-slate-700" />
          </div>
          <p className="font-medium">Syncing with radar...</p>
        </div>
      ) : flights.length === 0 ? (
        <div className="bg-slate-850 border border-dashed border-slate-700 rounded-2xl p-12 text-center">
          <p className="text-slate-500 text-sm">No active flights detected at the moment.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {flights.map((flight, idx) => (
            <motion.div 
              key={`${flight.flight.iata}-${idx}`} 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.05 }}
              className="bg-slate-850 border border-slate-800 rounded-xl p-4 hover:border-amber-500/30 transition-all shadow-lg"
            >
              <div className="flex justify-between items-start mb-3">
                <div>
                  <div className="flex items-center space-x-2">
                    <span className="font-bold text-white text-lg tracking-tight">{flight.airline.name}</span>
                    <span className="text-[10px] font-mono font-black bg-slate-900 px-2 py-0.5 rounded text-amber-500 border border-amber-500/10">
                      {flight.flight.iata}
                    </span>
                  </div>
                  <div className="text-sm text-slate-400 flex items-center mt-1 font-medium">
                    <SafeIcon icon={FiIcons.FiMapPin} className="mr-1.5 text-slate-600" />
                    To: <span className="text-slate-200 ml-1">{flight.arrival.airport} ({flight.arrival.iata})</span>
                  </div>
                </div>
                <div className={`text-[9px] uppercase font-black px-2.5 py-1 rounded-full border border-current tracking-widest ${getStatusColor(flight.flight_status)}`}>
                  {flight.flight_status}
                </div>
              </div>

              <div className="grid grid-cols-3 gap-2 border-t border-slate-800 pt-4 mt-2">
                <div className="text-center">
                  <p className="text-[9px] text-slate-500 font-black uppercase tracking-widest mb-1">Gate</p>
                  <p className="font-mono text-white font-bold text-sm">
                    {flight.departure.gate === 'TBD' ? (
                      <span className="text-slate-600 italic">TBD</span>
                    ) : (
                      flight.departure.gate
                    )}
                  </p>
                </div>
                <div className="text-center border-x border-slate-800">
                  <p className="text-[9px] text-slate-500 font-black uppercase tracking-widest mb-1">Departs</p>
                  <p className="font-mono text-amber-400 font-bold text-sm">
                    {new Date(flight.departure.scheduled).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-[9px] text-slate-500 font-black uppercase tracking-widest mb-1">Term</p>
                  <p className="font-mono text-white font-bold text-sm">{flight.departure.terminal}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}